import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import './AdminPage.css';

export default function AdminPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { t, i18n } = useTranslation();
  const isNew = id === 'new';

  const [form, setForm] = useState({
    first_name: '', last_name: '', dob: '',
    ssn: '', phone: '', email: '',
    address: '', city: '', postal_code: '',
    doctor: '', notes: '',
    sexe: '', pointure: '',
  });
  const [ordonnance, setOrdonnance] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew) return;
    fetch(`${API}/api/patients/${id}`)
      .then(r => r.json())
      .then(d => {
        setForm({
          first_name:  d.first_name  || '',
          last_name:   d.last_name   || '',
          dob:         d.dob         || '',
          ssn:         d.ssn         || '',
          phone:       d.phone       || '',
          email:       d.email       || '',
          address:     d.address     || '',
          city:        d.city        || '',
          postal_code: d.postal_code || '',
          doctor:      d.doctor      || '',
          notes:       d.notes       || '',
          sexe:        d.sexe        || '',
          pointure:    d.pointure    || '',
        });
        setConsultations(d.consultations || []);
      })
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError(t('admin.required_names'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (ordonnance) fd.append('ordonnance', ordonnance);

      if (isNew) {
        fd.append('practitioner_id', accounts[0]?.localAccountId || '');
        const res = await fetch(`${API}/api/patients`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        const { id: newId } = await res.json();
        navigate(`/patient/${newId}/consultation`);
      } else {
        const res = await fetch(`${API}/api/patients/${id}/admin`, { method: 'PUT', body: fd });
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        navigate(`/patient/${id}/consultation`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-loading">{t('common.loading')}</div>;

  const title = isNew
    ? t('admin.new_patient')
    : `${form.first_name} ${form.last_name.toUpperCase()}`;

  return (
    <div className="admin-page">
      <header className="admin-header">
        <button className="back-btn" onClick={() => navigate('/')}>{t('common.back')}</button>
        <p className="admin-eyebrow">{t('admin.eyebrow')}</p>
        <h1 className="admin-title">{title}</h1>
      </header>

      <div className="admin-body">

        <section className="admin-section">
          <div className="section-label">{t('admin.identity')}</div>
          <div className="fields-row">
            <div className="field">
              <label>{t('admin.first_name')} *</label>
              <input name="first_name" value={form.first_name} onChange={handleChange} placeholder="Jean" />
            </div>
            <div className="field">
              <label>{t('admin.last_name')} *</label>
              <input name="last_name" value={form.last_name} onChange={handleChange} placeholder="DUPONT" />
            </div>
            <div className="field">
              <label>{t('admin.dob')}</label>
              <input name="dob" type="date" value={form.dob} onChange={handleChange} />
            </div>
            <div className="field">
              <label>{t('admin.ssn')}</label>
              <input name="ssn" value={form.ssn} onChange={handleChange} placeholder="1 85 05 75 116 001 42" />
            </div>
            <div className="field">
              <label>{t('admin.sex')}</label>
              <select key={i18n.language} name="sexe" value={form.sexe} onChange={handleChange}>
                <option value="">—</option>
                <option value="M">{t('admin.sex_male')}</option>
                <option value="F">{t('admin.sex_female')}</option>
                <option value="autre">{t('admin.sex_other')}</option>
              </select>
            </div>
            <div className="field">
              <label>{t('admin.shoe_size')}</label>
              <input name="pointure" type="number" min="28" max="50"
                value={form.pointure} onChange={handleChange} placeholder="42" />
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="section-label">{t('admin.contact')}</div>
          <div className="fields-row">
            <div className="field">
              <label>{t('admin.phone')}</label>
              <input name="phone" value={form.phone} onChange={handleChange} placeholder="+34 600 000 000" />
            </div>
            <div className="field">
              <label>{t('admin.email')}</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="patient@email.com" />
            </div>
          </div>
          <div className="fields-row">
            <div className="field full">
              <label>{t('admin.address')}</label>
              <input name="address" value={form.address} onChange={handleChange} />
            </div>
          </div>
          <div className="fields-row">
            <div className="field">
              <label>{t('admin.city')}</label>
              <input name="city" value={form.city} onChange={handleChange} />
            </div>
            <div className="field">
              <label>{t('admin.postal_code')}</label>
              <input name="postal_code" value={form.postal_code} onChange={handleChange} />
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="section-label">{t('admin.doctor')}</div>
          <div className="field full">
            <label>{t('admin.doctor_name')}</label>
            <input name="doctor" value={form.doctor} onChange={handleChange} placeholder="Dr. García" />
          </div>
        </section>

        <section className="admin-section">
          <div className="section-label">{t('admin.ordonnance')}</div>
          <div className="field full">
            <label className="file-label">
              <input type="file" accept=".pdf,image/*" onChange={e => setOrdonnance(e.target.files[0])} />
              <span className="file-cta">
                {ordonnance ? `✓ ${ordonnance.name}` : t('admin.ordonnance_upload')}
              </span>
            </label>
          </div>
        </section>

        <section className="admin-section">
          <div className="section-label">{t('admin.notes')}</div>
          <div className="field full">
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={4}
              placeholder={t('admin.notes_placeholder')} />
          </div>
        </section>

        {!isNew && (
          <section className="admin-section">
            <div className="section-label">
              {t('admin.consult_history', { count: consultations.length })}
            </div>
            {consultations.length === 0 ? (
              <p className="admin-empty">{t('admin.no_consult')}</p>
            ) : (
              <ul className="consult-history">
                {[...consultations].reverse().map((c, i) => (
                  <li key={c.id || i} className="consult-history-item">
                    <span className="chi-date">
                      {c.date
                        ? new Date(c.date).toLocaleDateString(i18n.language, { day:'2-digit', month:'long', year:'numeric' })
                        : '—'}
                    </span>
                    <span className="chi-meta">
                      {t('admin.pain_short')} {c.pain_level ?? 0}/10 · {t('admin.handicap_short')} {c.handicap ?? 0}/10
                      {Array.isArray(c.pain_zones) && c.pain_zones.length > 0 && (
                        <> · {c.pain_zones.join(', ')}</>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {error && <div className="admin-error">{error}</div>}

        <div className="admin-actions">
          <button className="btn-ghost" onClick={() => navigate('/')}>{t('common.cancel')}</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? t('admin.saving') : isNew ? t('admin.create') : t('admin.save')}
          </button>
        </div>

      </div>
    </div>
  );
}